/**
 * 페어링 점검 — 현장 DB 에 등록된 무선 비상벨 → CCTV, 통로등 모듈 → CCTV 연결이 맞는지.
 * 비상벨: 벨 ID 없음·중복, 연결 CCTV 없음·없는 CCTV, 같은 층에 더 가까운 CCTV 가 있는 연결(확인 필요).
 * 통로등: 같은 통로등(MAC)이 여러 CCTV 에 등록, CCTV 에 안 붙은 모듈, 통로등이 하나도 없는 CCTV.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

import { QaPairingReport, SiteParkingQaService } from '../../services/SiteParkingQaService';
import { BELL_ISSUE_LABEL, describeBell } from './qaInspect';

interface Props {
  projectId: number;
  ip: string;
  /** CCTV 를 누르면 상세를 연다. */
  onOpen: (name: string) => void;
  /** 점검 결과가 바뀔 때마다 — 맵이 문제 있는 것만 칠하는 데 쓴다. */
  onResult?: (result: QaPairingReport | null) => void;
}

const Heading: React.FC<{ children: React.ReactNode; count: number; tone: 'error' | 'warning' }> = ({
  children,
  count,
  tone,
}) => (
  <Typography variant="body2" fontWeight={700} sx={{ mt: 1.5 }}>
    {children}{' '}
    <Box component="span" sx={{ color: count ? `${tone}.main` : 'success.main' }}>
      {count}
    </Box>
  </Typography>
);

const CctvChip: React.FC<{ name: string; onOpen: (name: string) => void }> = ({ name, onOpen }) => (
  <Chip size="small" variant="outlined" label={name} onClick={() => onOpen(name)} sx={{ minHeight: 32 }} />
);

const QaPairingPanel: React.FC<Props> = ({ projectId, ip, onOpen, onResult }) => {
  const [report, setReport] = useState<QaPairingReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setReport(await SiteParkingQaService.pairing(projectId, ip));
    } catch (e) {
      setError(e instanceof Error ? e.message : '페어링 점검 실패');
    } finally {
      setBusy(false);
    }
  }, [projectId, ip]);

  useEffect(() => {
    check();
  }, [check]);
  useEffect(() => {
    onResult?.(report);
  }, [report, onResult]);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }} data-testid="qa-pairing-bells">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mr: 'auto' }}>
            무선 비상벨 페어링
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
            onClick={check}
            disabled={busy}
            sx={{ minHeight: 44 }}
          >
            {busy ? '점검 중…' : '다시 점검'}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" component="p">
          현장 DB 의 벨 ID(custom_id)와 연결 CCTV 를 봅니다. 벨을 누르면 이 연결대로 그 CCTV 경광등이 깜빡입니다.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
        {report && (
          <>
            <Heading count={report.bells.length} tone="error">
              벨 {report.bellTotal}개 중 확인할 벨
            </Heading>
            {report.bells.length === 0 && (
              <Alert severity="success" sx={{ mt: 1 }}>
                모든 벨이 ID 를 갖고 같은 층에서 가장 가까운 CCTV 에 연결돼 있습니다.
              </Alert>
            )}
            {report.bells.map((b) => (
              <Box key={b.id} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }} data-testid="qa-pairing-bell">
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" fontWeight={700}>
                    {b.section} · {b.name}
                  </Typography>
                  {b.issues.map((i) => (
                    <Chip
                      key={i}
                      size="small"
                      color={i === 'not_nearest' ? 'warning' : 'error'}
                      label={BELL_ISSUE_LABEL[i]}
                    />
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  ID {b.customId || '없음'} → {b.cctv || '연결 없음'}
                </Typography>
                {describeBell(b).map((line) => (
                  <Typography key={line} variant="caption" component="p" sx={{ mt: 0.25 }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            ))}
          </>
        )}
      </Paper>

      {report && (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }} data-testid="qa-pairing-aisle">
          <Typography variant="subtitle1" fontWeight={700}>
            통로등 페어링
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p">
            현장 DB 에 CCTV 마다 등록된 통로등 모듈(통로등 {report.aisleTotal}개 · CCTV {report.cctvTotal}대)을
            봅니다. 조명서버 초기화 때 CCTV 가 알려 준 조명망이 들어갑니다.
          </Typography>

          <Heading count={report.dupMac.length} tone="error">
            같은 통로등이 여러 CCTV 에 등록
          </Heading>
          {report.dupMac.map((d) => (
            <Box key={d.mac} sx={{ py: 0.75 }} data-testid="qa-pairing-dupmac">
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }} component="div">
                MAC {d.mac}
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {d.cctvs.map((n) => (
                  <CctvChip key={n} name={n} onOpen={onOpen} />
                ))}
              </Stack>
            </Box>
          ))}

          <Heading count={report.unlinked.length} tone="error">
            CCTV 에 안 붙은 통로등 모듈
          </Heading>
          {report.unlinked.map((m) => (
            <Typography
              key={m.id}
              variant="caption"
              component="div"
              sx={{ fontFamily: 'monospace' }}
              data-testid="qa-pairing-unlinked"
            >
              모듈 {m.modId} · MAC {m.mac || '없음'} (light_modules id {m.id})
            </Typography>
          ))}

          <Divider sx={{ mt: 1.5 }} />
          <Heading count={report.noAisle.length} tone="warning">
            통로등이 하나도 없는 CCTV
          </Heading>
          <Typography variant="caption" color="text.secondary" component="p">
            현장에 통로등이 달려 있는데 여기 보이면 페어링이 안 된 것입니다.
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }} data-testid="qa-pairing-noaisle">
            {report.noAisle.map((n) => (
              <CctvChip key={n} name={n} onOpen={onOpen} />
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

export default QaPairingPanel;
